
import React, { useState, useMemo, useEffect } from 'react';
import { AircraftType } from '../types';
import { fleetApi } from '../services/fleetApi';

interface AircraftManagerProps {
  aircrafts: AircraftType[];
  setAircrafts: React.Dispatch<React.SetStateAction<AircraftType[]>>;
}

const AircraftManager: React.FC<AircraftManagerProps> = ({ aircrafts, setAircrafts }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState<Partial<AircraftType>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Real-time polling: Refresh fleet data every 5 seconds
  useEffect(() => {
    const fetchFleet = async () => {
      try {
        const updatedFleet = await fleetApi.getAircrafts();
        // Only update if not currently editing/adding to avoid conflicts
        if (!editingId && !isAdding && !isSaving) {
          setAircrafts(updatedFleet);
        }
      } catch (error) {
        console.error('Failed to refresh fleet data:', error);
      }
    };

    // Initial fetch
    fetchFleet();

    // Set up polling interval (5 seconds)
    const interval = setInterval(fetchFleet, 5000);

    return () => clearInterval(interval);
  }, [editingId, isAdding, isSaving, setAircrafts]);

  const filteredAircrafts = useMemo(() => {
    return aircrafts.filter(a => {
      const tailNumber = (a.tailNumber || '').toLowerCase();
      const name = (a.name || '').toLowerCase();
      const query = searchQuery.toLowerCase();
      const matchesSearch = tailNumber.includes(query) || name.includes(query);
      const matchesStatus = statusFilter === '' || a.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [aircrafts, searchQuery, statusFilter]);

  const handleEdit = (a: AircraftType) => {
    console.log('Editing aircraft:', { tailNumber: a.tailNumber, imei: (a as any).imei, full: a });
    setEditingId(a.id);
    // Ensure IMEI is included from aircraft data
    const aircraftWithImei = { ...a, imei: (a as any).imei || '' };
    setFormData(aircraftWithImei);
    setImagePreview(a.imageUrl || null);
    setIsAdding(false);
  };

  const handleAddNew = () => {
    setEditingId(null);
    setFormData({
      name: '',
      tailNumber: '5H-',
      hourlyRate: 1200,
      minBillableHours: 1.0,
      operatingCostPerHour: 600,
      maxPax: 12,
      crewCount: 2,
      fuelBurnLitersPerHour: 180,
      status: 'SERVICEABLE',
      totalAirframeHours: 0,
      totalBlockHours: 0,
      totalDutyHours: 0,
      lastLandedLocation: 'ARK',
      fuelStatus: { leftWing: 0, rightWing: 0, lastUpdated: new Date().toISOString() }
    });
    setImagePreview(null);
    setIsAdding(true);
  };

  const handleImageFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setImagePreview(base64String);
        setFormData({ ...formData, imageUrl: base64String });
      };
      reader.onerror = () => {
        alert('Failed to read image file');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!formData.tailNumber || formData.tailNumber === '5H-') {
      alert("Validation Error: Tail Number must be specified.");
      return;
    }
    
    setIsSaving(true);
    try {
      // Debug: Log what we're sending
      console.log('Saving aircraft data:', { 
        editingId, 
        isAdding,
        formDataKeys: Object.keys(formData),
        imei: formData.imei,
        fullFormData: formData 
      });
      
      if (isAdding) {
        const created = await fleetApi.createAircraft(formData);
        console.log('Created aircraft:', { imei: created.imei, full: created });
        setAircrafts(prev => [...prev, created]);
        setIsAdding(false);
        setFormData({});
        setImagePreview(null);
      } else if (editingId) {
        const updated = await fleetApi.updateAircraft(editingId, formData);
        console.log('Updated aircraft:', { imei: updated.imei, full: updated });
        setAircrafts(prev => prev.map(a => a.id === editingId ? updated : a));
        setEditingId(null);
        setFormData({});
        setImagePreview(null);
      }
    } catch (e) {
      console.error("Backend Uplink Error:", e);
      alert("Operational Error: Failed to commit airframe data to the registry. Backend node may be unreachable.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string, tailNumber: string) => {
    if (!confirm(`Are you sure you want to delete ${tailNumber}? This will deactivate the aircraft from the fleet.`)) {
      return;
    }

    setDeletingId(id);
    try {
      await fleetApi.deleteAircraft(id);
      setAircrafts(prev => prev.filter(a => a.id !== id));
    } catch (e) {
      console.error("Delete Error:", e);
      alert("Failed to delete aircraft. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-12 animate-in fade-in duration-700 pb-24">
      {/* Search & Filter Tactical Bar */}
      <div className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm flex flex-col lg:flex-row gap-6 items-center">
        <div className="flex-1 relative w-full">
          <svg className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input 
            type="text" 
            placeholder="Search Fleet Registry (Tail or Model)..." 
            className="w-full pl-16 pr-8 py-5 bg-slate-50 rounded-3xl text-sm font-bold border-none outline-none focus:ring-4 focus:ring-indigo-500/5 transition-all"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex w-full lg:w-auto gap-4">
          <select 
            className="flex-1 lg:flex-none px-8 py-5 bg-slate-50 rounded-3xl text-[10px] font-black uppercase tracking-widest border-none outline-none cursor-pointer appearance-none shadow-sm"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">All Flight Assets</option>
            <option value="SERVICEABLE">Serviceable</option>
            <option value="MAINTENANCE">Maintenance</option>
            <option value="AOG">AOG / Grounded</option>
          </select>
          <button 
            onClick={handleAddNew} 
            className="flex-1 lg:flex-none bg-slate-900 text-white px-10 py-5 rounded-3xl font-black text-[10px] uppercase tracking-widest hover:bg-black transition-all shadow-xl shadow-slate-200 active:scale-95"
          >
            + Register Airframe
          </button>
        </div>
      </div>

      {/* Assets Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-10">
        {filteredAircrafts.map(a => (
          <div key={a.id} className="bg-white rounded-[3.5rem] border border-slate-100 overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.02)] hover:shadow-2xl transition-all duration-500 group relative">
            <div className="h-64 relative overflow-hidden">
               <img 
                 src={a.imageUrl || 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?q=80&w=2069&auto=format&fit=crop'} 
                 className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" 
                 alt={a.tailNumber} 
               />
               <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent"></div>
               <div className="absolute bottom-8 left-10">
                  <h4 className="text-4xl font-black text-white tracking-tighter uppercase leading-none mb-2">{a.tailNumber}</h4>
                  <div className="flex items-center gap-3">
                     <p className="text-[10px] font-black text-white/60 uppercase tracking-[0.2em]">{a.name}</p>
                     <span className="w-1.5 h-1.5 rounded-full bg-white/30"></span>
                     <p className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em]">C208 Grand Caravan</p>
                  </div>
               </div>
               <div className={`absolute top-8 right-10 px-6 py-2.5 rounded-full text-[9px] font-black uppercase tracking-widest border border-white/20 backdrop-blur-xl shadow-2xl ${
                 a.status === 'SERVICEABLE' ? 'bg-emerald-500/90 text-white' : 
                 a.status === 'MAINTENANCE' ? 'bg-amber-500/90 text-white' : 'bg-rose-500/90 text-white'
               }`}>
                 {a.status.replace('_', ' ')}
               </div>
            </div>
            
            <div className="p-12 space-y-12">
               <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
                  <AircraftMetric label="Seats" value={a.maxPax} icon="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  <AircraftMetric label="Block Hours" value={`${a.totalBlockHours}h`} color="text-indigo-600" icon="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  <AircraftMetric label="Duty Cycle" value={`${a.totalDutyHours}h`} color="text-emerald-600" icon="M13 10V3L4 14h7v7l9-11h-7z" />
                  <AircraftMetric label="Next SVC" value={a.hoursToService ? `${a.hoursToService}h` : 'TBD'} color={a.hoursToService && a.hoursToService < 20 ? 'text-rose-600' : 'text-slate-900'} icon="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
               </div>
               
               <div className="flex justify-between items-center pt-10 border-t border-slate-50">
                  <div className="flex items-center gap-4">
                     <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 font-mono text-[10px] border border-slate-100">
                        {a.lastLandedLocation}
                     </div>
                     <div className="flex flex-col">
                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Current Station</p>
                        <p className="text-xs font-black text-slate-800 uppercase tracking-tight">Arusha Municipal (ARK)</p>
                     </div>
                  </div>
                  <div className="flex gap-3">
                    <button 
                      onClick={() => handleEdit(a)} 
                      className="px-10 py-5 bg-slate-900 text-white rounded-[2rem] text-[9px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 shadow-2xl shadow-indigo-100 transition-all active:scale-95"
                    >
                      Modify Registry
                    </button>
                    <button 
                      onClick={() => handleDelete(a.id, a.tailNumber)}
                      disabled={deletingId === a.id}
                      className="px-8 py-5 bg-rose-500 text-white rounded-[2rem] text-[9px] font-black uppercase tracking-[0.2em] hover:bg-rose-600 shadow-2xl shadow-rose-100 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deletingId === a.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
               </div>
            </div>
          </div>
        ))}
      </div>

      {/* Immersive Editor Modal */}
      {(editingId || isAdding) && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/80 backdrop-blur-xl p-6">
          <div className="bg-white rounded-[4rem] w-full max-w-4xl overflow-hidden shadow-[0_50px_100px_rgba(0,0,0,0.5)] animate-in zoom-in-95 duration-500 flex flex-col max-h-[90vh]">
             <div className="p-12 border-b border-slate-50 flex justify-between items-center bg-slate-900 text-white shrink-0">
                <div>
                  <h3 className="text-3xl font-black uppercase tracking-tight leading-none mb-3">
                    {isAdding ? 'Provision Airframe Node' : `Registry Node: ${formData.tailNumber}`}
                  </h3>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Secure Registry Transaction Layer</p>
                  </div>
                </div>
                <button onClick={() => {setEditingId(null); setIsAdding(false); setImagePreview(null);}} className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center text-white/40 hover:text-white transition-all group">
                  <svg className="w-8 h-8 group-hover:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
             </div>
             
             <div className="flex-1 p-12 space-y-12 overflow-y-auto custom-scrollbar">
                <section className="space-y-6">
                   <h5 className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.3em] border-b border-slate-100 pb-4">Aircraft Image</h5>
                   <div className="space-y-4">
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <EditInput 
                            label="Image URL" 
                            value={formData.imageUrl && !formData.imageUrl.startsWith('data:') ? formData.imageUrl : ''} 
                            placeholder="https://example.com/aircraft-image.jpg" 
                            onChange={v => {
                              setFormData({...formData, imageUrl: v});
                              setImagePreview(v || null);
                            }} 
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="px-6 py-5 bg-indigo-600 text-white rounded-2xl text-[9px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg"
                          >
                            Upload File
                          </button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageFileSelect}
                            className="hidden"
                          />
                        </div>
                      </div>
                      {(imagePreview || formData.imageUrl) && (
                        <div className="mt-4">
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3">Preview</p>
                          <div className="w-full h-48 rounded-2xl overflow-hidden border-2 border-slate-200">
                            <img 
                              src={imagePreview || formData.imageUrl} 
                              alt="Aircraft preview" 
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.src = 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?q=80&w=2069&auto=format&fit=crop';
                              }}
                            />
                          </div>
                          {formData.imageUrl?.startsWith('data:') && (
                            <p className="text-[8px] text-slate-400 mt-2">✓ Image loaded from local file</p>
                          )}
                        </div>
                      )}
                   </div>
                </section>

                <section className="space-y-6">
                   <h5 className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.3em] border-b border-slate-100 pb-4">Aeronautical Constants</h5>
                   <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                      <EditInput label="Registration" value={formData.tailNumber || ''} placeholder="5H-XXX" onChange={v => setFormData({...formData, tailNumber: v.toUpperCase()})} />
                      <EditInput label="Airframe Model" value={formData.name || ''} placeholder="e.g. Cessna Caravan" onChange={v => setFormData({...formData, name: v})} />
                      <div className="space-y-2">
                         <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-2">Airworthiness Status</label>
                         <select 
                           className="w-full p-5 bg-slate-50 rounded-2xl font-black text-xs text-slate-800 border-none outline-none appearance-none shadow-inner" 
                           value={formData.status || ''} 
                           onChange={e => setFormData({...formData, status: e.target.value as any})}
                         >
                            <option value="SERVICEABLE">Serviceable</option>
                            <option value="MAINTENANCE">In-Maintenance</option>
                            <option value="AOG">AOG (Grounded)</option>
                         </select>
                      </div>
                   </div>
                </section>

                <section className="space-y-6">
                   <h5 className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.3em] border-b border-slate-100 pb-4">Telemetry Integration</h5>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                         <EditInput 
                           label="v2Track Device ESN/IMEI" 
                           value={formData.imei || ''} 
                           placeholder="e.g. 5X200225000995" 
                           onChange={v => setFormData({...formData, imei: v.trim()})} 
                         />
                         <p className="text-[8px] font-medium text-slate-400 ml-2">
                            Enter the v2Track device ESN/IMEI to enable real-time telemetry tracking. 
                            Find this in your v2Track account under Device Management.
                         </p>
                      </div>
                   </div>
                </section>

                <section className="space-y-6">
                   <h5 className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.3em] border-b border-slate-100 pb-4">Commercial & Engineering Matrix</h5>
                   <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                      <EditInput label="Charter Rate ($/h)" type="number" value={formData.hourlyRate || 0} onChange={v => setFormData({...formData, hourlyRate: parseInt(v)})} />
                      <EditInput label="Ops Cost ($/h)" type="number" value={formData.operatingCostPerHour || 0} onChange={v => setFormData({...formData, operatingCostPerHour: parseInt(v)})} />
                      <EditInput label="Fuel Burn (L/h)" type="number" value={formData.fuelBurnLitersPerHour || 0} onChange={v => setFormData({...formData, fuelBurnLitersPerHour: parseInt(v)})} />
                      <EditInput label="Pax Capacity" type="number" value={formData.maxPax || 0} onChange={v => setFormData({...formData, maxPax: parseInt(v)})} />
                   </div>
                </section>

                <section className="space-y-6">
                   <h5 className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.3em] border-b border-slate-100 pb-4">Life-Cycle Tracking</h5>
                   <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                      <EditInput label="Total Airframe" type="number" value={formData.totalAirframeHours || 0} onChange={v => setFormData({...formData, totalAirframeHours: parseFloat(v)})} />
                      <EditInput label="Total Block" type="number" value={formData.totalBlockHours || 0} onChange={v => setFormData({...formData, totalBlockHours: parseFloat(v)})} />
                      <EditInput label="Total Duty" type="number" value={formData.totalDutyHours || 0} onChange={v => setFormData({...formData, totalDutyHours: parseFloat(v)})} />
                      <EditInput label="Next A-Check (h)" type="number" value={formData.hoursToService || 0} onChange={v => setFormData({...formData, hoursToService: parseFloat(v)})} />
                   </div>
                </section>
             </div>

             <div className="p-12 bg-slate-50 border-t border-slate-100 shrink-0">
                <button 
                  disabled={isSaving}
                  onClick={handleSave} 
                  className="w-full py-8 bg-indigo-600 text-white rounded-[2.5rem] font-black text-[11px] uppercase tracking-[0.4em] shadow-2xl shadow-indigo-200 hover:bg-indigo-700 transition-all disabled:opacity-50 active:scale-95"
                >
                  {isSaving ? 'Synchronizing with Fleet Node...' : 'Commit Operational Registry Update'}
                </button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AircraftMetric: React.FC<{ label: string; value: string | number; color?: string; icon: string }> = ({ label, value, color = 'text-slate-900', icon }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2">
       <svg className="w-3.5 h-3.5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d={icon}/></svg>
       <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
    </div>
    <p className={`text-xl font-black font-mono tracking-tighter leading-none ${color}`}>{value}</p>
  </div>
);

const EditInput: React.FC<{ label: string; value: any; onChange: (v: string) => void; placeholder?: string; type?: string }> = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div className="space-y-2">
    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-2">{label}</label>
    <input 
      type={type} 
      className="w-full p-5 bg-slate-50 rounded-2xl font-black text-xs text-slate-800 border-none outline-none shadow-inner focus:ring-4 focus:ring-indigo-500/5 transition-all" 
      value={value} 
      placeholder={placeholder}
      onChange={e => onChange(e.target.value)} 
    />
  </div>
);

export default AircraftManager;
